import { useState, useEffect, useCallback, useRef } from "react";
import Navbar from "../components/layout/Navbar";
import MobileNavbar from "../components/layout/MobileNavbar";
import useIsMobile from "../hooks/useIsMobile";
import api from "../api/client";

/* ---------- helpers ---------- */

function today() {
  return new Date().toISOString().slice(0, 10);
}

function formatDateHeader(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-PH", { month: "short", day: "numeric" });
}

/* ---------- Shared report page shell ---------- */

export default function SingleReportPage({ title, label, endpoint, backPath = "/reports", productFilter }) {
  const isMobile = useIsMobile();
  /* filters */
  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(today());
  const [product, setProduct] = useState(productFilter?.defaultValue || "all");
  const [agencies, setAgencies] = useState([]);
  const [selectedAgencies, setSelectedAgencies] = useState([]);
  const [agencyDropdownOpen, setAgencyDropdownOpen] = useState(false);
  const agencyRef = useRef(null);

  /* data */
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  /* close dropdown on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (agencyRef.current && !agencyRef.current.contains(e.target)) {
        setAgencyDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* fetch agency list (re-fetch when product changes) */
  useEffect(() => {
    const params = {};
    if (productFilter && product) params.product = product;
    api.get("/reports/agencies", { params }).then(({ data }) => {
      setAgencies(data);
      setSelectedAgencies([]);
    }).catch(() => {});
  }, [product, productFilter]);

  /* fetch report data */
  const fetchReport = useCallback(async () => {
    setLoading(true);
    const params = { date_from: dateFrom, date_to: dateTo };
    if (selectedAgencies.length > 0) {
      params.agencies = selectedAgencies.join(",");
    }
    if (productFilter && product) {
      params.product = product;
    }
    try {
      const { data } = await api.get(endpoint, { params });
      setReportData(data);
    } catch {
      // handled by interceptor
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedAgencies, endpoint, product, productFilter]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const toggleAgency = (name) => {
    setSelectedAgencies((prev) =>
      prev.includes(name) ? prev.filter((a) => a !== name) : [...prev, name]
    );
  };
  const selectAllAgencies = () => setSelectedAgencies([...agencies]);
  const clearAllAgencies = () => setSelectedAgencies([]);

  return (
    <div className="min-h-screen bg-gray-50">
      {isMobile ? <MobileNavbar /> : <Navbar />}

      <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-6">
        {/* Back link + title */}
        <div className="flex items-center gap-3 mb-4">
          <a href={backPath} className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Reports
          </a>
        </div>
        <h1 className="text-2xl font-bold text-gray-800 mb-4">{title}</h1>

        {/* ---- Filters ---- */}
        <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="border rounded px-3 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="border rounded px-3 py-1.5 text-sm"
            />
          </div>

          {/* Product filter */}
          {productFilter && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
              <select
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                className="border rounded px-3 py-1.5 text-sm bg-white min-w-[180px]"
              >
                {productFilter.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Agency multi-select */}
          <div className="relative" ref={agencyRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">Agency</label>
            <button
              onClick={() => setAgencyDropdownOpen((o) => !o)}
              className="border rounded px-3 py-1.5 text-sm bg-white min-w-[220px] text-left flex items-center justify-between"
            >
              <span className="truncate">
                {selectedAgencies.length === 0
                  ? "All Agencies"
                  : selectedAgencies.length === agencies.length
                  ? "All Agencies"
                  : `${selectedAgencies.length} selected`}
              </span>
              <svg className="w-4 h-4 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {agencyDropdownOpen && (
              <div className="absolute z-50 mt-1 w-72 bg-white border rounded shadow-lg max-h-64 overflow-auto">
                <div className="flex gap-2 px-3 py-2 border-b sticky top-0 bg-white">
                  <button onClick={selectAllAgencies} className="text-xs text-blue-600 hover:underline">Select All</button>
                  <button onClick={clearAllAgencies} className="text-xs text-blue-600 hover:underline">Clear All</button>
                </div>
                {agencies.map((name) => (
                  <label key={name} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={selectedAgencies.includes(name)}
                      onChange={() => toggleAgency(name)}
                      className="rounded"
                    />
                    <span className="truncate">{name}</span>
                  </label>
                ))}
                {agencies.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-400">No agencies found</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ---- Table ---- */}
        {loading && (
          <div className="text-center text-gray-500 py-8">Loading report…</div>
        )}

        {!loading && reportData && <ReportTable label={label} data={reportData} />}
      </div>
    </div>
  );
}

/* ---------- Report table ---------- */

function SortIcon({ direction }) {
  if (!direction) {
    return (
      <svg className="w-3 h-3 ml-1 inline opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    );
  }
  return direction === "asc" ? (
    <svg className="w-3 h-3 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
  ) : (
    <svg className="w-3 h-3 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function ReportTable({ label, data }) {
  const { dates, rows, grand_totals } = data;
  // sortKey: "name" | "total" | a date ISO string
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" ? "asc" : "desc"); // default: A-Z for name, high-to-low for numbers
    }
  };

  const sortedRows = [...rows].sort((a, b) => {
    if (!sortKey) return 0;
    let va, vb;
    if (sortKey === "name") {
      va = (a.name || "").toLowerCase();
      vb = (b.name || "").toLowerCase();
      return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    }
    if (sortKey === "total") {
      va = a.total;
      vb = b.total;
    } else {
      // date column
      va = a.counts[sortKey] || 0;
      vb = b.counts[sortKey] || 0;
    }
    return sortDir === "asc" ? va - vb : vb - va;
  });

  if (!rows || rows.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 py-8 text-center text-gray-400">No data for the selected filters.</div>
      </div>
    );
  }

  const thClass = "px-3 py-2 font-semibold text-gray-700 min-w-[80px] cursor-pointer select-none hover:bg-gray-200 transition-colors";

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th
                onClick={() => handleSort("name")}
                className={`px-3 py-2 text-left font-semibold text-gray-700 sticky left-0 bg-gray-100 z-10 min-w-[200px] cursor-pointer select-none hover:bg-gray-200 transition-colors`}
              >
                {label}
                <SortIcon direction={sortKey === "name" ? sortDir : null} />
              </th>
              {dates.map((d) => (
                <th
                  key={d}
                  onClick={() => handleSort(d)}
                  className={`${thClass} text-center`}
                >
                  {formatDateHeader(d)}
                  <SortIcon direction={sortKey === d ? sortDir : null} />
                </th>
              ))}
              <th
                onClick={() => handleSort("total")}
                className="px-3 py-2 text-center font-bold text-gray-800 bg-blue-50 min-w-[80px] cursor-pointer select-none hover:bg-blue-100 transition-colors"
              >
                Total
                <SortIcon direction={sortKey === "total" ? sortDir : null} />
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, idx) => (
              <tr key={row.name} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                <td className="px-3 py-2 font-medium text-gray-800 sticky left-0 z-10" style={{ backgroundColor: idx % 2 === 0 ? "white" : "#f9fafb" }}>
                  {row.name}
                </td>
                {dates.map((d) => (
                  <td key={d} className="px-3 py-2 text-center text-gray-700">
                    {row.counts[d] || 0}
                  </td>
                ))}
                <td className="px-3 py-2 text-center font-bold text-gray-800 bg-blue-50">
                  {row.total}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-blue-50 font-bold">
              <td className="px-3 py-2 text-gray-800 sticky left-0 bg-blue-50 z-10">Grand Total</td>
              {dates.map((d) => (
                <td key={d} className="px-3 py-2 text-center text-gray-800">
                  {grand_totals[d] || 0}
                </td>
              ))}
              <td className="px-3 py-2 text-center text-gray-900 bg-blue-100">
                {grand_totals.total || 0}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
